import { CalendarDays, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { useMemo } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { routeLink } from '../../../lib/route-link';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useSiteTimeZone } from '../../../lib/site-time-zone';
import { addDays, startOfDay } from '../../../lib/calendar/calendar-dates';
import { useCalendarItems } from '../../../lib/calendar/use-calendar-items';
import { useCalendarTitlePreference } from '../../../lib/calendar/use-calendar-title-preference';
import { itemsOfShownLayers, useShownLayers } from '../../../lib/calendar/use-shown-layers';
import { AgentEntryRow } from '../agents/AgentEntryRow';
import { Card, Typography } from '../ui';
import { itemTitle } from '../../../lib/calendar/item-text';
import { useCalendarFormat } from './use-calendar-format';
import styles from '../agents/FilesSummaryCard.module.css';

// A Space's Calendar tile: how much is on today and what is next, opening the Calendar narrowed to the
// Space. The line fills in when the reads land; the tile itself never waits for them.
export function CalendarSummaryCard({
  workspaceId,
  pageName,
  entryRow = false,
}: {
  workspaceId: string;
  /** Carried by the summary row's shared scope; the Calendar is narrowed by id. */
  workspaceSlug?: string;
  pageName: string;
  entryRow?: boolean;
}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const siteTimeZone = useSiteTimeZone();
  const timeZone = siteTimeZone.data || 'UTC';
  const format = useCalendarFormat(timeZone);
  const titlePreference = useCalendarTitlePreference();
  const now = Date.now();
  // From the clock on every render, so a page left open past midnight reads the new day.
  const day = startOfDay(now, timeZone);
  const today = useMemo(() => ({ from: day, to: addDays(day, 1, timeZone) }), [day, timeZone]);
  const data = useCalendarItems(today, workspaceId, { showEventTitles: titlePreference.showEventTitles });
  const { shown } = useShownLayers(data);

  const todays = itemsOfShownLayers(data.items, data.layers, shown).filter((item) => !item.settled);
  const next = todays.find((item) => !item.allDay && item.start >= now);
  // A source that failed would read as an empty day, so the line says what the tile is instead.
  const ready = data.sources.every((source) => !source.loading && source.failed.length === 0);
  const summary = !ready
    ? t('calendar.card.description')
    : [
        t('calendar.card.today', { count: todays.length }),
        next ? t('calendar.card.next', { time: format.time(next.start), title: itemTitle(next, t) }) : t('calendar.card.nothingToday'),
      ].join(' · ');

  const route = `/calendar?space=${encodeURIComponent(workspaceId)}`;
  const open = () => logPageClickBi({ page_name: pageName, button_name: 'open_calendar' });

  if (entryRow) {
    return <AgentEntryRow title={t('calendar.title')} subtitle={summary} href={route} onClick={open} testId="spaceCalendar" />;
  }

  return (
    <Card component={Link} interactive size="sm" className={styles.card} {...routeLink(basePath, route)} onClick={open}>
      <div className={styles.body}>
        <span className={styles.title}>
          <CalendarDays size={16} aria-hidden />
          <Typography variant="h4" color="strong" component="span" noWrap className={styles.titleText}>
            {t('calendar.title')}
          </Typography>
        </span>
        <Typography variant="p3" color="secondary" noWrap>
          {summary}
        </Typography>
      </div>
      <ChevronRight size={16} className={`${styles.chevron} dir-flip`} aria-hidden />
    </Card>
  );
}
